import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { useTabParam } from "../../lib/useTabParam";
import AddLegacyWarrantyModal from "./AddLegacyWarrantyModal";
import type { Deal } from "../../lib/types";
import { fmtInt } from "../../lib/analytics";
import DealLink from "../DealLink";

// ---------------------------------------------------------------------------
// Warranty — registration SLA + active claim queue
// ---------------------------------------------------------------------------
// Lifted out of QCBoard (May 9, 2026) so Robert + Jennifer can drive warranty
// work from the Dispatch Board next to PM Board / Trades / Punch List instead
// of having to bounce over to the QC & Reinspections board.
//
// Behavior unchanged from the QC version:
// - Registration SLA panel: closed_won deals built but not yet OC-registered,
//   bucketed red ≥55d / amber ≥45d / slate < 45d since build_completed_date
//
// Aug 26, 2026 — the panel header used to read "N not yet registered" off
// needsReg.length, but the query carried .limit(30), so it printed "30" against
// a real backlog of 547. A page size presented as a total: the exact invariant
// the widget-board work calls out (true counts in the header — never render a
// .limit() as the count).
//
// "547" would have been just as useless though. Only 7 of those are still
// inside the 60-day registration window; 540 are already blown, 375 of them
// over a year old — mostly legacy/backfilled builds nobody will ever register.
// So the panel now counts the two groups separately: the in-window set is the
// work, the past-window pile is history. Both numbers are exact (count:'exact'),
// neither is a slice length.
//
// The list is also now ordered OLDEST build first — most days elapsed, closest
// to blowing the window. It used to be newest-first, which put the least urgent
// row on top.
// - Filter pills (Active / Resolved / Declined-or-Void / All), URL-backed via
//   ?warranty_status= so back-from-deal preserves the filter
// - Per-row card: homeowner + title, category chip, priority chip, status chip,
//   photo count, "N days open" with red/amber/slate buckets
// - Click navigates to /deal/{id}?tab=warranty for full claim management
// - Sorted by updated_at desc (most recent activity first)
//
// Aug 26, 2026 — "+ Legacy job" button. Roofs we replaced in the Zoho era never
// converted, so a warranty call on one of those homes had no deal to hang off.
// The modal creates the bare contact + property at closed_won with the warranty
// fields filled in and nothing else.
//
// The write runs through the create_legacy_warranty_deal definer RPC, so the
// button is gated on that RPC's gate: warranty writers (Jennifer, Robert) OR a
// role that could already insert a deal (admin / asm / restore_manager /
// team_lead). Robert is roof_team_manager, which is in NO deals INSERT policy —
// the RPC is what gives him a path without handing him blanket deal-insert.
// Keep this check and the RPC's gate in sync; the RPC is the one that matters.

const SLA_DAYS = 60;
// Plenty for a 60-day window of builds; a hard cap so one bad month can't
// render hundreds of rows. Anything beyond it is surfaced as "+N more".
const REG_DISPLAY_CAP = 25;

/** Local calendar date N days back as YYYY-MM-DD, for DATE-column compares. */
function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Whole calendar days between a YYYY-MM-DD and today — the same arithmetic
 * Postgres does for `current_date - build_completed_date`. The old version did
 * `Date.now() - new Date(iso)`, which parses a date-only string as UTC midnight
 * and so reads ~4h older than it is in ET; that inflates the age near a day
 * boundary and can tip a row into the red bucket a day early.
 */
function daysSinceISO(iso: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return 0;
  const then = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today - then) / 86_400_000);
}

const WARRANTY_STATUS_KEYS = ["active", "resolved", "declined", "all"] as const;
type WStatusKey = typeof WARRANTY_STATUS_KEYS[number];
const ACTIVE_STATUSES = new Set(["claim_filed", "in_progress"]);
const RESOLVED_STATUSES = new Set(["claim_approved"]);
const DECLINED_STATUSES = new Set(["claim_denied", "voided", "expired"]);

export default function WarrantyTab() {
  type ClaimRow = {
    id: string;
    deal_id: string;
    title: string | null;
    category: string | null;
    reported_issue: string | null;
    priority: string | null;
    status: string;
    claim_date: string | null;
    resolution_date: string | null;
    created_at: string;
    updated_at: string;
  };
  type DealMeta = { id: string; homeowner_name: string | null };
  type RegRow = Pick<Deal, "id" | "homeowner_name" | "claim_number" | "build_completed_date">;
  const [claims, setClaims] = useState<ClaimRow[] | null>(null);
  const [dealsById, setDealsById] = useState<Record<string, DealMeta>>({});
  const [photoCounts, setPhotoCounts] = useState<Record<string, number>>({});
  const [needsReg, setNeedsReg] = useState<RegRow[] | null>(null);
  // Exact counts, not slice lengths. inWindow = still registerable, past = blown.
  const [regInWindow, setRegInWindow] = useState(0);
  const [regPastWindow, setRegPastWindow] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useTabParam<WStatusKey>(
    "warranty_status",
    "active",
    WARRANTY_STATUS_KEYS,
  );
  const [showAddLegacy, setShowAddLegacy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [isWarrantyWriter, setIsWarrantyWriter] = useState(false);
  const navigate = useNavigate();
  const { member, isSuperUser } = useAuth();

  // Mirror of the create_legacy_warranty_deal gate — see the header note.
  const canAddLegacy =
    isSuperUser ||
    isWarrantyWriter ||
    ["admin", "asm", "restore_manager", "team_lead"].includes(member?.role ?? "");

  // warranty_writers is a finance_settings row (Jennifer + Robert today), so
  // adding a reviewer stays a one-row update with no deploy.
  useEffect(() => {
    const email = member?.email;
    if (!email) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("finance_settings")
        .select("value_jsonb")
        .eq("key", "warranty_writers")
        .maybeSingle();
      const emails = Array.isArray(data?.value_jsonb) ? (data!.value_jsonb as string[]) : [];
      if (!cancelled) {
        setIsWarrantyWriter(emails.some((e) => e.toLowerCase() === email.toLowerCase()));
      }
    })();
    return () => { cancelled = true; };
  }, [member?.email]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // build_completed_date is a DATE column — compare against a bare ISO
      // string. Never new Date() a date-only value (the May 9 off-by-one).
      const windowStart = isoDaysAgo(SLA_DAYS);

      const [
        { data: c, error: e1 },
        { data: r, error: e2, count: inWindowCount },
        { error: e3, count: totalCount },
      ] = await Promise.all([
        supabase
          .from("warranty_claims")
          .select("id,deal_id,title,category,reported_issue,priority,status,claim_date,resolution_date,created_at,updated_at")
          .order("updated_at", { ascending: false }),
        // The work: still inside the window. Oldest build first = most urgent.
        // count:'exact' is the TRUE in-window total even though .limit() caps
        // the rows we render — that separation is the whole point of this fix.
        supabase
          .from("deals")
          .select("id,homeowner_name,claim_number,build_completed_date", { count: "exact" })
          .eq("stage", "closed_won")
          .is("warranty_registered_date", null)
          .not("build_completed_date", "is", null)
          .gte("build_completed_date", windowStart)
          .order("build_completed_date", { ascending: true })
          .limit(REG_DISPLAY_CAP),
        // The whole backlog, head:true — we want the number, not 547 rows.
        // Written out rather than sharing a builder with the query above:
        // re-calling .select() on an already-selected builder to flip on
        // head:true is ambiguous, and a wrong count here is the bug we are
        // fixing.
        supabase
          .from("deals")
          .select("id", { count: "exact", head: true })
          .eq("stage", "closed_won")
          .is("warranty_registered_date", null)
          .not("build_completed_date", "is", null),
      ]);
      if (e1 ?? e2 ?? e3) {
        if (!cancelled) setErr((e1 ?? e2 ?? e3)!.message);
        return;
      }
      const claimsList = (c ?? []) as ClaimRow[];
      const dealIds = Array.from(new Set(claimsList.map((cl) => cl.deal_id).filter(Boolean)));

      const [{ data: dealRows }, { data: photoRows }] = await Promise.all([
        dealIds.length > 0
          ? supabase.from("deals").select("id,homeowner_name").in("id", dealIds)
          : Promise.resolve({ data: [] as DealMeta[] }),
        dealIds.length > 0
          ? supabase
              .from("deal_documents")
              .select("warranty_claim_id")
              .in("deal_id", dealIds)
              .in("file_type", ["warranty_photo", "warranty_document"])
              .is("superseded_at", null)
          : Promise.resolve({ data: [] as Array<{ warranty_claim_id: string | null }> }),
      ]);

      const dealsMap: Record<string, DealMeta> = {};
      for (const d of (dealRows ?? []) as DealMeta[]) dealsMap[d.id] = d;

      const counts: Record<string, number> = {};
      for (const p of (photoRows ?? []) as Array<{ warranty_claim_id: string | null }>) {
        if (p.warranty_claim_id) counts[p.warranty_claim_id] = (counts[p.warranty_claim_id] ?? 0) + 1;
      }

      if (!cancelled) {
        setClaims(claimsList);
        setDealsById(dealsMap);
        setPhotoCounts(counts);
        setNeedsReg((r ?? []) as RegRow[]);
        setRegInWindow(inWindowCount ?? 0);
        setRegPastWindow(Math.max(0, (totalCount ?? 0) - (inWindowCount ?? 0)));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // Realtime — claims, photos, and registration list refresh on changes
  useEffect(() => {
    const channel = supabase
      .channel("dispatch_warranty_tab")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "warranty_claims" },
        () => {
          // Reload everything — quick query, realistic data volumes (claims will
          // be small for a long time)
          (async () => {
            const { data: c } = await supabase
              .from("warranty_claims")
              .select("id,deal_id,title,category,reported_issue,priority,status,claim_date,resolution_date,created_at,updated_at")
              .order("updated_at", { ascending: false });
            if (c) setClaims(c as ClaimRow[]);
          })();
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  if (err) return <Panel error>Couldn&apos;t load warranty — {err}</Panel>;
  if (!claims || !needsReg) return <Panel>Loading warranty…</Panel>;

  const counts = {
    active: claims.filter((c) => ACTIVE_STATUSES.has(c.status)).length,
    resolved: claims.filter((c) => RESOLVED_STATUSES.has(c.status)).length,
    declined: claims.filter((c) => DECLINED_STATUSES.has(c.status)).length,
    all: claims.length,
  };

  const filteredClaims = claims.filter((c) => {
    if (statusFilter === "active") return ACTIVE_STATUSES.has(c.status);
    if (statusFilter === "resolved") return RESOLVED_STATUSES.has(c.status);
    if (statusFilter === "declined") return DECLINED_STATUSES.has(c.status);
    return true;
  });

  return (
    <div className="space-y-4">
      <Panel>
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-semibold text-bardin-ink">
            Warranty registration (60-day SLA)
          </h3>
          <span className="text-xs text-slate-500">
            {fmtInt(regInWindow)} still in the {SLA_DAYS}-day window
          </span>
        </div>
        {regInWindow === 0 ? (
          <Empty>Nothing is inside the {SLA_DAYS}-day registration window right now.</Empty>
        ) : (
          <ul className="divide-y divide-slate-100">
            {needsReg.map((d) => {
              const daysSince = d.build_completed_date ? daysSinceISO(d.build_completed_date) : 0;
              const bucket =
                daysSince >= 55 ? "red" : daysSince >= 45 ? "amber" : "slate";
              return (
                <li key={d.id} className="py-2 text-sm flex items-center justify-between">
                  <div>
                    <div className="font-medium text-bardin-ink">
                      <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                    </div>
                    <div className="text-xs text-slate-500">
                      Built {d.build_completed_date}
                      {d.claim_number ? ` · #${d.claim_number}` : ""}
                    </div>
                  </div>
                  <span
                    className={
                      "text-xs px-2 py-0.5 rounded " +
                      (bucket === "red"
                        ? "bg-red-100 text-red-700"
                        : bucket === "amber"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-slate-100 text-slate-600")
                    }
                  >
                    {daysSince}d
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {(regPastWindow > 0 || regInWindow > needsReg.length) && (
          <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
            {regInWindow > needsReg.length && (
              <span>+{fmtInt(regInWindow - needsReg.length)} more inside the window · </span>
            )}
            {regPastWindow > 0 && (
              <span>
                {fmtInt(regPastWindow)} already past the {SLA_DAYS}-day window — mostly legacy builds,
                not a live queue.
              </span>
            )}
          </div>
        )}
      </Panel>

      <Panel>
        <div className="flex items-baseline justify-between gap-3 mb-3">
          <h3 className="text-sm font-semibold text-bardin-ink">Warranty claims</h3>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-slate-500">
              {fmtInt(filteredClaims.length)} of {fmtInt(claims.length)} total
            </span>
            {canAddLegacy && (
              <button
                onClick={() => setShowAddLegacy(true)}
                title="Record a roof we replaced before the CRM so a warranty can hang off it"
                className="px-2.5 py-1 text-xs font-medium rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
              >
                + Legacy job
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          <FilterPill
            label="Active"
            count={counts.active}
            active={statusFilter === "active"}
            onClick={() => setStatusFilter("active")}
            tone="amber"
          />
          <FilterPill
            label="Resolved"
            count={counts.resolved}
            active={statusFilter === "resolved"}
            onClick={() => setStatusFilter("resolved")}
            tone="emerald"
          />
          <FilterPill
            label="Declined / Void"
            count={counts.declined}
            active={statusFilter === "declined"}
            onClick={() => setStatusFilter("declined")}
            tone="slate"
          />
          <FilterPill
            label="All"
            count={counts.all}
            active={statusFilter === "all"}
            onClick={() => setStatusFilter("all")}
            tone="slate"
          />
        </div>

        {filteredClaims.length === 0 ? (
          <Empty>
            {statusFilter === "active"
              ? "No active warranty claims."
              : statusFilter === "resolved"
                ? "No approved warranty claims yet."
                : statusFilter === "declined"
                  ? "No declined or voided claims."
                  : "No warranty claims have been filed."}
          </Empty>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filteredClaims.slice(0, 50).map((c) => (
              <ClaimRowCard
                key={c.id}
                claim={c}
                deal={dealsById[c.deal_id]}
                photoCount={photoCounts[c.id] ?? 0}
                onClick={() =>
                  navigate(`/deal/${c.deal_id}?tab=warranty`, {
                    state: { from: window.location.pathname + window.location.search },
                  })
                }
              />
            ))}
          </ul>
        )}
      </Panel>

      {showAddLegacy && (
        <AddLegacyWarrantyModal
          onClose={() => setShowAddLegacy(false)}
          onCreated={() => {
            setShowAddLegacy(false);
            setReloadKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}

function FilterPill({
  label,
  count,
  active,
  onClick,
  tone,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  tone: "amber" | "emerald" | "slate";
}) {
  const baseStyles =
    tone === "amber"
      ? "border-amber-200 bg-amber-50 text-amber-700"
      : tone === "emerald"
        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
        : "border-slate-200 bg-slate-50 text-slate-600";
  const activeStyles =
    tone === "amber"
      ? "bg-amber-500 text-white border-amber-600"
      : tone === "emerald"
        ? "bg-emerald-600 text-white border-emerald-700"
        : "bg-slate-700 text-white border-slate-800";
  return (
    <button
      onClick={onClick}
      className={
        "px-2.5 py-1 text-xs font-medium rounded border transition " +
        (active ? activeStyles : baseStyles + " hover:bg-slate-100")
      }
    >
      {label} <span className="ml-1 opacity-70">{count}</span>
    </button>
  );
}

function ClaimRowCard({
  claim,
  deal,
  photoCount,
  onClick,
}: {
  claim: {
    id: string;
    deal_id: string;
    title: string | null;
    category: string | null;
    reported_issue: string | null;
    priority: string | null;
    status: string;
    claim_date: string | null;
    resolution_date: string | null;
    created_at: string;
    updated_at: string;
  };
  deal: { id: string; homeowner_name: string | null } | undefined;
  photoCount: number;
  onClick: () => void;
}) {
  const isActive = ACTIVE_STATUSES.has(claim.status);
  const daysOpen = isActive
    ? Math.floor((Date.now() - new Date(claim.created_at).getTime()) / 86_400_000)
    : 0;
  const ageBucket = isActive
    ? daysOpen >= 7
      ? "red"
      : daysOpen >= 3
        ? "amber"
        : "slate"
    : "slate";
  const statusChip = ((): { label: string; className: string } => {
    switch (claim.status) {
      case "claim_filed":
        return { label: "Filed", className: "bg-amber-100 text-amber-700 border-amber-200" };
      case "in_progress":
        return { label: "In progress", className: "bg-blue-100 text-blue-700 border-blue-200" };
      case "claim_approved":
        return { label: "Approved", className: "bg-emerald-100 text-emerald-700 border-emerald-200" };
      case "claim_denied":
        return { label: "Denied", className: "bg-slate-200 text-slate-700 border-slate-300" };
      case "voided":
        return { label: "Voided", className: "bg-slate-200 text-slate-700 border-slate-300" };
      case "expired":
        return { label: "Expired", className: "bg-slate-200 text-slate-700 border-slate-300" };
      default:
        return { label: claim.status, className: "bg-slate-100 text-slate-600 border-slate-200" };
    }
  })();
  const titleText = claim.title || claim.reported_issue || "(no title)";

  return (
    <li
      onClick={onClick}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}
      className="py-2.5 text-sm cursor-pointer hover:bg-slate-50 transition px-1 -mx-1 rounded"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-bardin-ink truncate">
            {deal?.homeowner_name ?? "(no name)"}
          </div>
          <div className="text-sm text-slate-700 truncate mt-0.5">{titleText}</div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <span className={"text-xs px-2 py-0.5 rounded border " + statusChip.className}>
              {statusChip.label}
            </span>
            {claim.category && (
              <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                {claim.category.replace(/_/g, " ")}
              </span>
            )}
            {claim.priority === "high" && (
              <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">
                high priority
              </span>
            )}
            {photoCount > 0 && (
              <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                📎 {photoCount}
              </span>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-xs text-slate-500">
            Filed {claim.claim_date ?? "—"}
          </div>
          {isActive && (
            <span
              className={
                "inline-block text-xs px-2 py-0.5 rounded mt-1 " +
                (ageBucket === "red"
                  ? "bg-red-100 text-red-700"
                  : ageBucket === "amber"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-slate-100 text-slate-600")
              }
            >
              {daysOpen}d open
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

// ── Local primitives (mirror QCBoard's shared Panel/Empty so this file stays
// self-contained and we don't have to thread an import path through). ─────
function Panel({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div className={error ? "bg-red-50 text-red-700 p-4 rounded" : "bg-white rounded-xl shadow p-5"}>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-sm text-slate-500 py-2">{children}</div>;
}
